import React, { useEffect, useMemo, useState } from "react";
import { useParams, useNavigate } from "react-router-dom";
import api, { extractErrorMessage } from "@/lib/api";
import { useLang } from "@/i18n";
import { useAuth } from "@/contexts/AuthContext";
import PanelLayout from "@/components/layout/PanelLayout";
import QuestionRenderer from "@/components/QuestionRenderer";
import BottomPageNav from "@/components/nav/BottomPageNav";
import { isQARole, qaBackTarget } from "@/pages/user/UserDashboard";
import { toast } from "sonner";
import { ArrowLeft, Send, CheckCircle2, Lock, AlertTriangle } from "lucide-react";

const Questionnaire = () => {
  const { id } = useParams();
  const { lang, t, pickI18n, tQuestionnaireTitle } = useLang();
  const { user } = useAuth();
  const navigate = useNavigate();
  const [q, setQ] = useState(null);
  const [answers, setAnswers] = useState({});
  const [loading, setLoading] = useState(false);
  const [done, setDone] = useState(false);
  // existingResponse !== null means the user already submitted before.
  // existingChecked guards against rendering an empty form during the
  // brief window where the GET /me/responses/:id call is still in flight.
  const [existingResponse, setExistingResponse] = useState(null);
  const [existingChecked, setExistingChecked] = useState(false);

  useEffect(() => {
    let cancelled = false;
    setExistingChecked(false);
    setExistingResponse(null);

    api.get(`/questionnaires/${id}`).then((r) => {
      if (cancelled) return;
      setQ(r.data);
      // Only kick off the existing-response lookup once we have the
      // schema. Doing it in parallel would risk rendering the empty form
      // for a flicker if the schema landed first.
      api.get(`/me/responses/${id}`)
        .then((res) => {
          if (cancelled) return;
          setExistingResponse(res.data || null);
          setAnswers(res.data?.answers || {});
        })
        .catch((err) => {
          // 404 just means "user hasn't submitted yet" — that's the
          // happy path for the empty form; only log non-404s.
          if (err?.response?.status !== 404) {
            console.warn("Could not load existing response", err);
          }
        })
        .finally(() => { if (!cancelled) setExistingChecked(true); });
    }).catch((err) => {
      toast.error(extractErrorMessage(err));
      navigate("/app");
    });
    return () => { cancelled = true; };
  }, [id, navigate]);

  const sections = useMemo(() => {
    if (!q) return [];
    const map = new Map();
    for (const question of q.questions) {
      const key = pickI18n(question, "section") || "";
      if (!map.has(key)) map.set(key, []);
      map.get(key).push(question);
    }
    return Array.from(map.entries());
  }, [q, lang, pickI18n]);

  const submit = async (e) => {
    e.preventDefault();
    if (!q) return;
    for (const question of q.questions) {
      if (question.required && answers[question.id] === undefined) {
        toast.error(t("common.requiredField"));
        return;
      }
    }
    setLoading(true);
    try {
      await api.post("/responses", { questionnaire_id: q.id, answers });
      setDone(true);
      toast.success(t("user.thanksTitle"));
    } catch (err) {
      const detail = err?.response?.data?.detail;
      const isProfileBlock =
        typeof detail === "string" && detail.toLowerCase().includes("not available for your profile");
      if (err?.response?.status === 403 && isProfileBlock) {
        toast.error(t("phase2.profileNotAvailable"));
      } else {
        toast.error(extractErrorMessage(err));
      }
    } finally { setLoading(false); }
  };

  if (!q || !existingChecked) return <PanelLayout><div className="tsw-section py-12">{t("common.loading")}</div></PanelLayout>;

  if (done) {
    const showQABack = isQARole(user?.role);
    return (
      <PanelLayout>
        <div className="tsw-section py-20 max-w-md text-center">
          <CheckCircle2 className="w-12 h-12 text-emerald-600 mx-auto" />
          <h1 className="text-3xl font-display font-bold mt-4 text-slate-900">{t("user.thanksTitle")}</h1>
          <p className="text-slate-600 mt-2">{t("user.thanksBody")}</p>
          <div className="mt-6 flex flex-col sm:flex-row gap-3 justify-center">
            <button onClick={() => navigate("/app")} className="tsw-btn-primary" data-testid="back-to-dashboard">{t("common.dashboard")}</button>
            {showQABack && (
              <button
                type="button"
                onClick={() => navigate(qaBackTarget(user.role))}
                className="tsw-btn-primary"
                data-testid="qa-back-to-menu-success"
              >
                <ArrowLeft className="w-4 h-4 rtl:rotate-180" /> {t("nav.backToQAMenu")}
              </button>
            )}
          </div>
        </div>
      </PanelLayout>
    );
  }

  // ============ COMPLETED — read-only view ============
  // The user already submitted this questionnaire. The CRM-side answers
  // are pre-loaded so the user can see what they answered. Sensitive
  // answers (health / injury / psych / wellbeing / minor) are stored on
  // a separate `research_responses` collection and are deliberately NOT
  // round-tripped here — that's the platform's PII rule. Those fields
  // simply render with a "Sensitive answer recorded" placeholder.
  if (existingResponse) {
    const answeredCount = Object.keys(existingResponse.answers || {}).length;
    const submittedAt = existingResponse.created_at
      ? new Date(existingResponse.created_at).toLocaleString()
      : null;
    const completedHeading = t("phase2.completedHeading");
    const completedSub = t("phase2.completedSub");
    const submittedLabel = t("phase2.submittedAtLabel");
    const answeredLabel = t("phase2.answersRecorded");
    const sensitiveNote = t("phase2.sensitiveAnswerHidden");
    const missingNote = t("phase2.noSavedValue");

    return (
      <PanelLayout>
        <div className="tsw-section py-10 max-w-3xl">
          <button onClick={() => navigate("/app")} className="tsw-btn-ghost mb-3" data-testid="questionnaire-back">
            <ArrowLeft className="w-4 h-4 rtl:rotate-180" /> {t("common.back")}
          </button>

          <div className="rounded-xl border border-emerald-200 bg-emerald-50/50 p-5 mb-6 flex items-start gap-3" data-testid="questionnaire-completed-banner">
            <CheckCircle2 className="w-6 h-6 text-emerald-600 mt-0.5" />
            <div>
              <div className="font-display font-semibold text-emerald-900">{completedHeading}</div>
              <div className="text-sm text-emerald-800 mt-0.5">{completedSub}</div>
              <div className="text-xs text-emerald-700/80 mt-2 flex flex-wrap gap-x-4 gap-y-1">
                {submittedAt && <span><b>{submittedLabel}:</b> {submittedAt}</span>}
                <span><b>{answeredCount}</b> {answeredLabel}</span>
              </div>
            </div>
          </div>

          <h1 className="font-display font-bold text-3xl sm:text-4xl text-slate-900">
            {tQuestionnaireTitle(q)}
          </h1>
          <p className="text-slate-600 mt-2">{pickI18n(q, "description") || q.description_en}</p>

          <div className="mt-8 space-y-8" data-testid="questionnaire-readonly">
            {sections.map(([section, questions]) => (
              <section key={section} className="tsw-card p-6 space-y-5">
                {section && (
                  <div className="border-b border-slate-100 pb-3 mb-2">
                    <div className="tsw-overline">{t("user.sectionLabel")}</div>
                    <div className="font-display font-semibold text-slate-900">{section}</div>
                  </div>
                )}
                {questions.map((question) => {
                  const v = answers[question.id];
                  const sensitive = (question.sensitive_tags || []).length > 0;
                  // Sensitive answers are intentionally never returned
                  // from the GET endpoint, so v will be undefined here —
                  // surface a privacy-aware placeholder instead of an
                  // empty input.
                  let display;
                  if (sensitive && v === undefined) {
                    display = <div className="text-xs italic text-slate-500 inline-flex items-center gap-1.5"><Lock className="w-3.5 h-3.5" /> {sensitiveNote}</div>;
                  } else if (v === undefined || v === null || v === "") {
                    display = <div className="text-xs italic text-slate-400">{missingNote}</div>;
                  } else if (Array.isArray(v)) {
                    display = (
                      <div className="flex flex-wrap gap-1.5">
                        {v.length === 0 && <div className="text-xs italic text-slate-400">{missingNote}</div>}
                        {v.map((item, i) => (
                          <span key={`${item}-${i}`} className="tsw-chip bg-emerald-50 text-emerald-800 border border-emerald-200">{String(item)}</span>
                        ))}
                      </div>
                    );
                  } else {
                    display = <div className="text-slate-800 whitespace-pre-wrap break-words">{String(v)}</div>;
                  }

                  return (
                    <div key={question.id} className="space-y-1.5" data-testid={`ro-question-${question.id}`}>
                      <div className="text-sm font-medium text-slate-700">
                        {pickI18n(question, "label") || question.label_en}
                        {question.required && <span className="text-red-500 ms-1">*</span>}
                        {sensitive && (
                          <span className="ms-2 text-[10px] uppercase tracking-wider text-slate-500">
                            {t("phase2.sensitiveBadgeShort")}
                          </span>
                        )}
                      </div>
                      <div className="rounded-md border border-slate-200 bg-slate-50/60 px-3 py-2 text-sm">
                        {display}
                      </div>
                    </div>
                  );
                })}
              </section>
            ))}
          </div>
          <BottomPageNav />
        </div>
      </PanelLayout>
    );
  }

  // ============ NEW SUBMISSION — editable form ============
  return (
    <PanelLayout>
      <div className="tsw-section py-10 max-w-3xl">
        <button onClick={() => navigate(-1)} className="tsw-btn-back mb-3" data-testid="questionnaire-back">
          <ArrowLeft className="w-4 h-4 rtl:rotate-180" /> {t("common.back")}
        </button>
        <h1 className="font-display font-bold text-3xl sm:text-4xl text-slate-900">
          {tQuestionnaireTitle(q)}
        </h1>
        <p className="text-slate-600 mt-2">{pickI18n(q, "description") || q.description_en}</p>

        <form onSubmit={submit} className="mt-8 space-y-8" data-testid="questionnaire-form">
          {sections.map(([section, questions]) => (
            <section key={section} className="tsw-card p-6 space-y-5">
              {section && (
                <div className="border-b border-slate-100 pb-3 mb-2">
                  <div className="tsw-overline">{t("user.sectionLabel")}</div>
                  <div className="font-display font-semibold text-slate-900">{section}</div>
                </div>
              )}
              {questions.map((question) => (
                <QuestionRenderer
                  key={question.id}
                  question={question}
                  value={answers[question.id]}
                  onChange={(v) => setAnswers((a) => ({ ...a, [question.id]: v }))}
                />
              ))}
            </section>
          ))}

          <div className="flex justify-end">
            <button type="submit" disabled={loading} className="tsw-btn-primary" data-testid="questionnaire-submit">
              <Send className="w-4 h-4" /> {loading ? t("common.sending") : t("common.submit")}
            </button>
          </div>
        </form>
        <BottomPageNav />
      </div>
    </PanelLayout>
  );
};

export default Questionnaire;
